import { AvatarSize } from '@fluentui/react-components';
import React, { CSSProperties, FC, useContext, useMemo } from 'react';

import { getColorsByID } from '../Utils';
import { AvatarContext } from './AvatarContext';
import { ColorAvatarProps } from './ColorAvatar';
import { LazyAvatar } from './LazyAvatar';

/**
 * @deprecated Map legacy northstar string sizes to v9 numeric AvatarSize values.
 * Remove once all consumers pass numeric AvatarSize.
 **/
const NORTHSTAR_SIZE_MAP: Record<string, AvatarSize> = {
  smallest: 20,
  smaller: 24,
  small: 28,
  medium: 32,
  large: 48, // Northstar: 44px → closest Fluent v9 size
  larger: 64, // Northstar: 64px
  largest: 96, // Northstar: 96px
};

export interface LazyAvatarUserProps
  extends Omit<ColorAvatarProps, 'backgroundColor' | 'color' | 'size'> {
  userId: string;
  /**
   * Accepts v9 numeric AvatarSize. Also accepts legacy northstar string sizes (smallest … largest)
   * for backward compatibility — prefer numeric values.
   **/
  size?: AvatarSize | (string & {});
  /** @deprecated Not supported in v9. Accepted for backward compatibility. */
  status?: Record<string, any>;
  /** @deprecated Use `style` instead. Northstar styles shorthand. */
  styles?: CSSProperties | Record<string, any>;
}

/**
 * Renders the photo of the user given by the user id.
 * If no photo exists, the initials will be calculated by the value given as "name".
 *
 * Note:
 * This component requires an AvatarContext to pick an existing photo
 * or to dispatch a load picture function if no photo was loaded yet.
 *
 * @return {*}
 */
export const LazyAvatarUser: FC<LazyAvatarUserProps> = ({
  userId,
  size,
  status: _status,
  styles: _styles,
  ...rest
}: any): any => {
  const { byUser, loadUserPicture } = useContext(AvatarContext);
  const colors = getColorsByID(userId);
  const picture = byUser[userId];
  const isPictureRequested = useMemo(() => picture && picture.isRequested, [picture]);

  const pictureUrl = useMemo(() => picture?.url, [picture?.url]);

  // Map legacy string sizes to v9 numeric sizes
  const resolvedSize = typeof size === 'string' ? NORTHSTAR_SIZE_MAP[size] ?? 32 : size;

  return (
    <LazyAvatar
      {...rest}
      size={resolvedSize}
      key={`lazy-user-avatar-${userId}`}
      backgroundColor={`${colors.color}`}
      color={colors.textColor}
      image={{ src: pictureUrl }}
      isLoaded={isPictureRequested}
      load={() => loadUserPicture(userId)}
    />
  );
};
